// SettingsNavigationBar.tsx
import React, { useState, MouseEvent, useMemo } from "react";
import {
    IconButton, Menu, MenuItem, Tooltip, Divider, Collapse, ListItemIcon, Box, Chip, Switch, FormControlLabel, Typography
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigation } from "../Context/Contexts";
import { useTranslation } from "react-i18next";
import useRoles from "../hooks/useRoles";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

type Params = { [key: string]: any };

type SettingsItem = {
    title: string;
    to: string;
    disabled?: boolean;
    params?: Params;
};

type SettingsSection = {
    title: string;
    items: SettingsItem[];
    collapsedByDefault?: boolean;
};

const SettingsNavigationBar: React.FC = () => {
    const { navigate } = useNavigation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { t } = useTranslation();
    const { hasRole } = useRoles();

    const handleClick = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const toHref = (to: string, params?: Params) =>
        `${to}${params ? "?" + new URLSearchParams(params as Record<string, string>).toString() : ""}`;

    const goto = (to: string, params?: Params) => {
        navigate({ path: to, params });
        handleClose();
    };

    const sections: SettingsSection[] = useMemo(() => [

        {
            title: t("common.general"),
            collapsedByDefault: false,
            items: [
                { title: t("common.myUser"), to: "/admin/user/edit", params: { loadMyUser: true } },
                { title: t("common.company"), to: "/settings/company/list", disabled: !hasRole("bo_admin") },
                { title: t("common.country"), to: "/settings/country/list", disabled: !hasRole("bo_admin") },
                { title: t("common.currency"), to: "/settings/currency/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.language"), to: "/settings/language/list", disabled: !hasRole("bo_admin"), advanced: true }
            ]
        },
        {
            title: t("common.businessSettings"),
            collapsedByDefault: true,
            items: [
                { title: t("common.accountPlan"), to: "/settings/account/list", disabled: !hasRole("bo_admin") },
                { title: t("common.paymentTerminal"), to: "/settings/paymentterminal/list", disabled: !hasRole("bo_admin") },
                { title: t("common.postingSetup"), to: "/settings/postingsetup/list", disabled: !hasRole("bo_admin") },
                { title: t("common.salesperson"), to: "/settings/salesperson/list", disabled: !hasRole("bo_admin") },
                { title: t("common.shop"), to: "/settings/shop/list", disabled: !hasRole("bo_admin") },
                { title: t("common.terminal"), to: "/settings/terminal/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.paymentMethod"), to: "/settings/paymentmethod/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.paymentProviderConfig"), to: "/settings/paymentproviderconfig/list", disabled: !hasRole("bo_admin"), advanced: true }
            ]
        },
        {
            title: t("common.warehouse"),
            collapsedByDefault: true,
            items: [
                { title: t("common.location"), to: "/settings/location/list", disabled: !hasRole("bo_admin") },
                { title: t("common.printer"), to: "/settings/printer/list", disabled: !hasRole("bo_admin") },
                { title: t("common.shippingAgent"), to: "/settings/shippingagent/list", disabled: !hasRole("bo_admin") }
            ]
        },
        {
            title: t("common.administration"),
            collapsedByDefault: true,
            items: [
                // { title: t("common.metrics"), to: "/admin/metrics/view", disabled: !hasRole("bo_admin") },
                { title: t("common.users"), to: "/admin/user/list", disabled: !hasRole("bo_admin") },
                { title: t("common.integrations"), to: "/integration/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.systemLog"), to: "/admin/systemlog/list", disabled: !hasRole("bo_admin"), advanced: true }
            ]
        },
        {
            title: t("common.item"),
            // keep your earlier behavior: hide whole Item section behind Advanced
            collapsedByDefault: true,
            advanced:true, 
            items: [
                { title: t("common.itemLabels"), to: "/inventory/labeljournal/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.colourGroup"), to: "/purchase/item/colourgroup/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.sizeGroup"), to: "/purchase/item/sizegroup/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.lengthGroup"), to: "/purchase/item/lengthgroup/list", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.itemGroup"), to: "/purchase/settings/itemgroup", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.itemCategory"), to: "/purchase/settings/itemcategory", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.productGroup"), to: "/purchase/settings/productgroup", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.program"), to: "/purchase/settings/program", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.collection"), to: "/purchase/settings/collection", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.gender"), to: "/purchase/settings/gender", disabled: !hasRole("bo_admin"), advanced: true },
                { title: t("common.statNo"), to: "/settings/statno/list", disabled: !hasRole("bo_admin"), advanced: true }
            ]
        }
    ], [hasRole, t]);

    // expanded state per section
    const [expanded, setExpanded] = useState<Record<string, boolean>>(
        () => Object.fromEntries(sections.map(s => [s.title, !(s.collapsedByDefault ?? false)]))
    );
    const [showAdvanced, setShowAdvanced] = useState(false);

    const toggleSection = (title: string) =>
        setExpanded(prev => ({ ...prev, [title]: !prev[title] }));

    const renderItem = (item: SettingsItem) => (
        <MenuItem
            key={item.title}
            disabled={item.disabled ?? false}
            onClick={() => goto(item.to, item.params)}
            component="a"
            href={toHref(item.to, item.params)}
            sx={{
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                gap: 1,
            }}
        >
            {item.title}
        </MenuItem>
    );



    return (
        <>
            <Tooltip title={t("common.settings")}>
                <IconButton onClick={handleClick} color="secondary">
                    <SettingsIcon />
                </IconButton>
            </Tooltip>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {sections.map((section, idx) => {
                    const isOpen = !!expanded[section.title];
                    const visibleItems = section.items.length;

                    return (
                        <React.Fragment key={section.title}>
                     {/*       {idx > 0 && <Divider />}*/}

                            {/* Clickable section header */}
                            <MenuItem
                                onClick={() => toggleSection(section.title)}
                                aria-expanded={isOpen}
                                aria-controls={`${section.title}-collapse`}
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <ExpandMoreIcon
                                        fontSize="small"
                                        sx={{
                                            transition: "transform 160ms ease",
                                            transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)"
                                        }}
                                    />
                                </ListItemIcon>
                                <Typography variant="caption" sx={{ flex: 1 }}>
                                    {section.title}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                            {visibleItems}
                                         
                                        </Box>
                                    }
                                    sx={{
                                        height: 20,
                                        "& .MuiChip-label": { px: 0.75, fontSize: 12, display: "flex", alignItems: "center" }
                                    }}
                                />
                            </MenuItem>

                            {/* Section contents */}
                            <Collapse in={isOpen} unmountOnExit timeout="auto">
                                <Box sx={{ pt: 0.5 }}>
                                    {section.items.map(renderItem)}
                                </Box>
                            </Collapse>
                        </React.Fragment>
                    );
                })}

             
            </Menu>
        </>
    );
};

export default SettingsNavigationBar;
